import * as React from 'react';
import Box from '@mui/material/Box';
import { useNavigate } from 'react-router';
import { requirementsApi } from '../services/api';
import BreakdownTab from '../components/itemDetails/BreakdownTab';
import { useAuth } from '../contexts/AuthContext';
import { Ic } from '../components/shell/Icons';
import { resolveIdDisplay } from '../utils/itemId';
import { formatRelativeTime } from '../utils/formatHistory';
import { usePref, setPref } from '../services/prefs';
import { getActiveOrg, getActiveProject } from '../services/authToken';
import { iamApi } from '../services/iamApi';
import { navClickHandler } from '../utils/navClick';
import ConfirmDialog from '../components/dialogs/ConfirmDialog';
import ProjectActionBar from '../components/project/ProjectActionBar';
import LinkTypeDrawer from '../components/dashboard/LinkTypeDrawer';
import useReviewActivity from '../hooks/useReviewActivity';

/**
 * Dashboard / overview screen — built to the agreed mockup
 * (docs/design/specimen/dashboard.html): KPI strip + "Needs your attention" +
 * "Coverage by level" + "Recent activity". All figures are REAL aggregates
 * computed from the org's requirements (no fabricated demo numbers): if a panel
 * has no data it renders an honest empty state.
 */

// #428: the status/verified/container/area/flatten/descendant-count helpers this
// view used to run over the full eager tree now live server-side in
// core/requirements/project_summary.py (GET /items/summary) — the view renders
// the returned aggregates.
//
// F-21 / REQ-6107 (#581, D-01): the single-item "Verify"/normAttrs helper that
// used to live here is DELETED, not repurposed — see the removed
// handleMarkVerified/handleVerifyAll/doVerifyAll below. Per the owner's DECIDED
// resolution on #581: the dashboard's bulk "Verify all" (and this per-row twin)
// is not renamed to "Approve all" — one-click mass sign-off is dropped outright
// in favour of a future filtered review queue (#146) that approves items
// individually with context. `rows.dashboard.verified/unverified/pct` and
// `by_status` (below) are unchanged SHAPES, but as of #581 B1 are now
// server-computed from the `item_approvals` rollup
// (`core/requirements/project_summary.py`'s `build_project_summary` /
// `SqlAlchemyDatabase.summary_rollup`, sourced from `feedback.kpi_verified`) —
// NOT the retired free-text status attribute. This view needed no change for
// that fix: it only ever rendered the aggregate the backend computed.

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
}

// #441: a collapsible holistic-metric card. The COUNT (badge) renders from the
// cheap summary payload on first paint; the DETAIL body lazy-loads on the FIRST
// expand only (native <details> → works without JS, keyboard-accessible). The
// parent owns the shared /coverage + /quality fetches so several cards backed by
// the same analysis expand from one request.
function MetricCard({ icon, title, badge, badgeTone = 'neutral', testId,
                     open, onOpenChange, loading, error, ready, children }) {
  return (
    <details
      className="metric"
      data-testid={testId}
      open={open}
      onToggle={(e) => onOpenChange(e.currentTarget.open)}
    >
      <summary className="mhead">
        <span className="mi" aria-hidden="true">{icon}</span>
        <span className="mt">{title}</span>
        <span className={`badge ${badgeTone}`} data-testid={`${testId}-count`}>{badge}</span>
        <svg className="chev2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <path d="M9 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </summary>
      <div className="mbody" data-detail>
        {loading && <div className="mloading" data-testid={`${testId}-loading`}><span className="spin" /> Loading…</div>}
        {!loading && error && <div className="t-body" style={{ color: 'var(--error)', padding: '10px 0' }}>{error}</div>}
        {!loading && !error && ready && children}
      </div>
    </details>
  );
}

// A detail list for a metric card — each requirement is a CLICKABLE row showing its
// id + TITLE (not a bare id chip), with the id-display tooltip and an optional
// trailing note (e.g. staleness age). Lists every item the (server-capped, 100)
// detail carries — no arbitrary client "+N more" truncation; if the true total
// exceeds the cap we say so.
function ItemList({ items, total, noteOf, showHumanIds, itemHref, onNavigate }) {
  const list = items || [];
  if (list.length === 0) {
    return <div className="t-body muted3" style={{ padding: '6px 0' }}>None — nothing to flag. 🎉</div>;
  }
  return (
    <div className="gaplist">
      {list.map((it) => {
        const disp = resolveIdDisplay(it, showHumanIds);
        const note = noteOf ? noteOf(it) : null;
        return (
          <a
            key={it.id}
            className="gaprow-lnk"
            href={itemHref(it.id)}
            onClick={navClickHandler(() => onNavigate(it.id))}
            title={disp.secondary || ''}
          >
            <span className="gapid">{disp.primary}</span>
            <span className="gaptitle">{it.title || 'Untitled'}</span>
            {note && <span className="gapnote">{note}</span>}
          </a>
        );
      })}
      {(total || 0) > list.length && (
        <div className="t-body muted3" style={{ padding: '6px 0 0', fontSize: 11 }}>
          Showing {list.length} of {total}.
        </div>
      )}
    </div>
  );
}

export default function DashboardHome() {
  const navigate = useNavigate();
  const { user, activeOrg } = useAuth();
  const [rows, setRows] = React.useState(null);
  const [error, setError] = React.useState(null);
  const showHumanIds = usePref('showHumanIds');
  const savedProject = usePref('dashboardProject');
  // `savedProject` is consulted ONLY to pick the initial scope inside the project-list
  // effect below. It must not be one of that effect's triggers: `handleProjectChange`
  // writes this very pref on every selector change, so listing it would make picking a
  // project re-run `iamApi.myProjects(org)` — a network round trip per selection, for a
  // value the effect only reads as a fallback default. Latest-value ref instead.
  const savedProjectRef = React.useRef(savedProject);
  React.useEffect(() => { savedProjectRef.current = savedProject; }, [savedProject]);
  const [projects, setProjects] = React.useState([]);
  // #439: the selector defaults to a SPECIFIC project, never the org-wide
  // roll-up — that aggregate is the expensive one and shouldn't fire just from
  // opening the dashboard. `null` means "scope not resolved yet" (distinct from
  // '' = explicit whole-org): the summary fetch waits for a concrete scope so we
  // never kick off an org-wide aggregate only to overwrite it a tick later. A
  // saved '' (user last chose whole-org) is treated as unresolved, so whole-org
  // is never restored as the default — the user re-selects it each session.
  const [selectedProject, setSelectedProject] = React.useState(savedProject || null);
  // In-app confirmation modal (replaces window.confirm). Holds the descriptor for
  // the pending action — { title, message, severity, confirmLabel, confirmColor,
  // testId, onConfirm } — or null when nothing is awaiting confirmation.
  const [confirm, setConfirm] = React.useState(null);
  // #441: lazy-loaded detail for the collapsible metric cards. /coverage backs the
  // Isolated / Leaf-gap / Links-by-type cards; /quality backs Review-staleness.
  // Each fetches ONCE on first expand and resets when the project scope changes.
  const [coverage, setCoverage] = React.useState({ state: 'idle', data: null, error: null });
  const [quality, setQuality] = React.useState({ state: 'idle', data: null, error: null });

  // #381: reload the project list after copy/archive so the selector reflects reality.
  const loadProjects = React.useCallback(() => {
    const org = getActiveOrg();
    (org ? iamApi.myProjects(org) : Promise.resolve([]))
      .then((list) => setProjects((list || []).map((p) => ({ id: p.id, name: p.name || p.title || p.id }))))
      .catch(() => { /* keep the current list on a refresh failure */ });
  }, []);

  // #460: depend on the REACTIVE activeOrg from AuthContext, not a one-shot getActiveOrg()
  // read at mount. On a hard-load / deep-link to /analytics the org isn't resolved yet at
  // first render, so the old []-deps effect fetched an empty list and NEVER retried when
  // AuthContext later resolved the org -> a permanently empty selector. Re-running on
  // activeOrg fixes that.
  React.useEffect(() => {
    let cancelled = false;
    const org = activeOrg || getActiveOrg();
    const fetch = org ? iamApi.myProjects(org) : Promise.resolve([]);
    fetch
      .then((list) => {
        if (cancelled) return;
        const projs = (list || []).map((p) => ({ id: p.id, name: p.name || p.title || p.id }));
        setProjects(projs);
        // #439: resolve the default scope — last saved project if it still
        // exists, else the app's active project, else the first project. Never
        // the org-wide roll-up ('') by default. When there are no projects at
        // all, '' is the only honest scope.
        setSelectedProject((cur) => {
          if (cur && projs.some((p) => p.id === cur)) return cur;
          const saved = savedProjectRef.current;
          if (saved && projs.some((p) => p.id === saved)) return saved;
          const active = getActiveProject();
          if (active && projs.some((p) => p.id === active)) return active;
          return projs.length ? projs[0].id : '';
        });
      })
      .catch(() => { if (!cancelled) setProjects([]); });
    return () => { cancelled = true; };
    // #460: re-fetch when the org resolves after a hard-load. `savedProject` is read via
    // its ref (see the note where it is declared) so it stays a default, not a trigger.
  }, [activeOrg]);

  const handleProjectChange = (e) => {
    const val = e.target.value;
    setSelectedProject(val);
    setPref('dashboardProject', val);
  };

  // #428: the dashboard renders BOUNDED server aggregates (GET /items/summary)
  // instead of fetching + flattening the whole eager tree per view open.
  React.useEffect(() => {
    // #439: wait until the default scope is resolved (null) before fetching, so
    // opening the dashboard never fires the expensive org-wide aggregate just to
    // replace it a tick later once the last/first project resolves.
    if (selectedProject === null) return undefined;
    let live = true;
    setRows(null);
    setError(null);
    // #441: a scope change invalidates any lazily-loaded metric detail — bump the
    // seq refs so an in-flight fetch for the OLD project can't land, and reset to
    // 'idle' so the effects below re-fetch for any card still open.
    covSeq.current += 1;
    qualSeq.current += 1;
    setCoverage({ state: 'idle', data: null, error: null });
    setQuality({ state: 'idle', data: null, error: null });
    const pid = selectedProject || null;
    // #501: defer the summary aggregate to idle time so it yields the main thread to the
    // requirements tree's initial paint + /me/projects fetch on the home route. The two
    // render independently, but on the throttled dev box the aggregate otherwise competes
    // for CPU during first paint. requestIdleCallback runs it just after paint; setTimeout
    // is the fallback where it's unavailable (Safari / jsdom).
    const schedule = (typeof window !== 'undefined' && window.requestIdleCallback)
      ? window.requestIdleCallback : (cb) => setTimeout(cb, 0);
    const unschedule = (typeof window !== 'undefined' && window.cancelIdleCallback)
      ? window.cancelIdleCallback : clearTimeout;
    const handle = schedule(() => {
      requirementsApi.getItemsSummary(pid)
        .then((summary) => { if (live) setRows(summary); })
        .catch((e) => { if (live) setError(e.message || 'Failed to load'); });
    });
    return () => { live = false; unschedule(handle); };
  }, [selectedProject]);

  // #441: which collapsible metric cards are open. Isolated / Leaf-gap /
  // Links-by-type all read the SAME /coverage analysis, so one open card of that
  // group triggers a single shared fetch; Review-staleness reads /quality.
  const [openCards, setOpenCards] = React.useState(() => new Set());
  const setCardOpen = React.useCallback((key, isOpen) => {
    setOpenCards((s) => {
      if (s.has(key) === isOpen) return s;
      const n = new Set(s);
      if (isOpen) n.add(key); else n.delete(key);
      return n;
    });
  }, []);
  const coverageWanted = openCards.has('isolated') || openCards.has('leaf-gap') || openCards.has('links');
  const qualityWanted = openCards.has('review-stale');

  // #441: lazy loaders. The effects below fire them only when a backing card is
  // open AND the state is 'idle', so each analysis is fetched at most once per
  // scope; a monotonic seq guards against a slow response for a previous scope.
  const covSeq = React.useRef(0);
  const qualSeq = React.useRef(0);
  const loadCoverage = React.useCallback(() => {
    const pid = selectedProject || null;
    const seq = (covSeq.current += 1);
    setCoverage({ state: 'loading', data: null, error: null });
    requirementsApi.getCoverage(pid)
      .then((data) => { if (covSeq.current === seq) setCoverage({ state: 'ready', data, error: null }); })
      .catch((e) => { if (covSeq.current === seq) setCoverage({ state: 'error', data: null, error: e.message || 'Failed to load' }); });
  }, [selectedProject]);
  const loadQuality = React.useCallback(() => {
    const pid = selectedProject || null;
    const seq = (qualSeq.current += 1);
    setQuality({ state: 'loading', data: null, error: null });
    requirementsApi.getQuality(pid)
      .then((data) => { if (qualSeq.current === seq) setQuality({ state: 'ready', data, error: null }); })
      .catch((e) => { if (qualSeq.current === seq) setQuality({ state: 'error', data: null, error: e.message || 'Failed to load' }); });
  }, [selectedProject]);

  React.useEffect(() => {
    if (coverageWanted && coverage.state === 'idle') loadCoverage();
  }, [coverageWanted, coverage.state, loadCoverage]);
  React.useEffect(() => {
    if (qualityWanted && quality.state === 'idle') loadQuality();
  }, [qualityWanted, quality.state, loadQuality]);

  // #342 Phase 2 — open review-feedback for the selected project (the queue is
  // per-project; "All projects" shows a neutral prompt). Best-effort.
  const [feedback, setFeedback] = React.useState(null); // { comments, truncated } | null
  // A FAILED load is its own state, not an empty queue (F-21 / #581, swept 2026-07-28).
  // The catch here used to `setFeedback({comments: [], truncated: false})`, which renders
  // "No open review feedback. 🎉" — an explicit all-clear, with a party emoji,
  // manufactured from a network error. That is the silent-omission class in its worst
  // direction: it does not merely withhold information, it asserts the opposite of what
  // is known, and a reviewer acts on it by moving on.
  const [feedbackError, setFeedbackError] = React.useState(false);
  const [feedbackReload, setFeedbackReload] = React.useState(0);
  React.useEffect(() => {
    let live = true;
    setFeedback(null);
    setFeedbackError(false);
    if (!selectedProject) return undefined;
    requirementsApi.getProjectFeedback(selectedProject, { status: 'open' })
      .then((res) => { if (live) setFeedback(res || { comments: [], truncated: false }); })
      .catch(() => { if (live) setFeedbackError(true); });
    return () => { live = false; };
  }, [selectedProject, feedbackReload]);

  const rawName = (user?.display_name || user?.email?.split('@')[0] || 'there').split(' ')[0];
  const name = rawName.charAt(0).toUpperCase() + rawName.slice(1);

  // #428: map the server summary onto the shape this view renders. All figures
  // stay REAL aggregates — they're just computed once server-side now.
  const m = React.useMemo(() => {
    if (!rows) return null;
    return {
      total: rows.totals.leaves,
      containers: rows.totals.containers,
      verified: rows.dashboard.verified,
      unverified: rows.dashboard.unverified,       // { total, ids, rows }
      pct: rows.dashboard.pct,
      areas: rows.dashboard.areas,
      recent: rows.dashboard.recent,
      pendingArchive: rows.archive_proposed,       // { total, ids, rows }
      metrics: rows.metrics || null,               // #441 cheap counts for the collapsible cards
      roots: rows.roots || [],                     // #442 top-level items → one BreakdownTab each
    };
  }, [rows]);

  // N-21 / REQ-6133 (#609): live refresh for each root's Breakdown panel below.
  // `m.roots` is scoped by EXACTLY the same rule `reload()` fetches
  // `/items/summary` with (below): `selectedProject` when one is picked, else
  // every project the org-wide view spans. `projects` (the selector's own list,
  // from `iamApi.myProjects`) is this view's equivalent of the tree's
  // `myProjects` — the filter-independent source — for that whole-org case.
  const watchedProjectIds = React.useMemo(() => (
    selectedProject ? [selectedProject] : (projects || []).map((p) => p.id)
  ), [selectedProject, projects]);
  const { changeCount: reviewChangeCount } = useReviewActivity(watchedProjectIds);

  const itemHref = (id) => `/item/${id}`;
  // #449: which "Links by type" chip's drawer is open — { type, total } | null.
  // Reads `selectedProject` fresh on every render (passed straight through as
  // the drawer's `projectId` prop below), so a project switch while the
  // drawer is open re-scopes its fetches the same way /coverage does.
  const [linkTypeDrawer, setLinkTypeDrawer] = React.useState(null);
  const goItem = (id) => navigate(itemHref(id));
  const barColor = (p) => (p >= 70 ? 'var(--success)' : p >= 50 ? 'var(--warning)' : 'var(--error)');

  const reload = () => {
    const pid = selectedProject || null;
    requirementsApi.getItemsSummary(pid)
      .then((summary) => setRows(summary))
      .catch(() => { /* silent reload */ });
  };

  // #381: project-level action buttons on the dashboard, mirroring the tree's
  // project right-click menu — so the project-scoped actions are reachable from
  // here too (they were tree-only). Each reuses the SAME endpoint the tree does.
  const selectedProjectName = projects.find((p) => p.id === selectedProject)?.name || '';
  // #381: after a project-scoped change (new item / copy / archive) from the shared
  // ProjectActionBar, refresh both the metrics and the project list.
  const onProjectChanged = () => { reload(); loadProjects(); };
  // #347: the accepted item is removed from LOCAL state only — accepting ONE
  // proposal must not trigger a full-tree reload. When the archived item is a
  // container, a refetch drops its descendants (and, once the pending list empties,
  // hides the whole card), which read to users as "it accepted them all". The KPI
  // memo (`m`) recomputes from the remaining rows.
  const doConfirmArchive = async (id) => {
    try {
      await requirementsApi.confirmArchive(id);
      // #347: remove the accepted item from LOCAL state only (no full reload —
      // see the note above); the KPI memo recomputes from the remaining summary.
      setRows((prev) => (prev ? {
        ...prev,
        archive_proposed: {
          ...prev.archive_proposed,
          total: Math.max(0, prev.archive_proposed.total - 1),
          ids: prev.archive_proposed.ids.filter((x) => x !== id),
          rows: prev.archive_proposed.rows.filter((r) => r.id !== id),
        },
      } : prev));
    } catch (e) { setError(e?.response?.data?.detail || 'Archive failed'); }
  };
  const handleConfirmArchive = async (id) => {
    // #347 (follow-up): the cascade IS intended — archiving a parent archives its
    // whole subtree — but that surprised users, so confirm via an in-app modal
    // (not a native alert) with the descendant count (now server-computed, #428).
    const row = (rows?.archive_proposed?.rows || []).find((r) => r.id === id);
    const n = row ? (row.descendant_count || 0) : 0;
    if (n > 0) {
      setConfirm({
        title: 'Archive this item and its children?',
        message: `This item has ${n} child item${n === 1 ? '' : 's'}. Archiving it will also archive ${n === 1 ? 'that item' : 'all of them'} and any of their descendants.`,
        severity: 'warning', confirmColor: 'error', confirmLabel: 'Archive all',
        testId: 'archive-cascade-dialog',
        onConfirm: () => doConfirmArchive(id),
      });
      return;
    }
    await doConfirmArchive(id);
  };

  // #348: "Archive all" / "Verify all" — iterate the existing per-id calls over
  // the FULL list (not the sliced 6), tolerate partial failure, then reload to
  // reflect reality. The confirmation is an in-app modal (not a native alert).
  const doArchiveAll = async (ids) => {
    try {
      const { ok, failed } = await requirementsApi.confirmArchiveMany(ids);
      if (failed.length) setError(`Archived ${ok.length} of ${ids.length}; ${failed.length} failed.`);
    } catch (e) { setError(e?.response?.data?.detail || 'Archive all failed'); }
    reload();
  };
  const handleArchiveAll = () => {
    if (!m || m.pendingArchive.total === 0) return;
    const ids = m.pendingArchive.ids;  // the FULL set, not the 6 rendered rows (#428)
    setConfirm({
      title: `Archive all ${ids.length} pending item${ids.length === 1 ? '' : 's'}?`,
      message: 'This cannot be undone. Any children of these items will also be archived.',
      severity: 'warning', confirmColor: 'error', confirmLabel: 'Archive all',
      testId: 'archive-all-dialog',
      onConfirm: () => doArchiveAll(ids),
    });
  };
  const handleCancelArchive = async (id) => {
    try { await requirementsApi.cancelArchive(id); reload(); }
    catch (e) { setError(e?.response?.data?.detail || 'Cancel failed'); }
  };

  return (
    <div style={{ flex: 1, minWidth: 0, overflow: 'auto' }}>
      <div className="pagewide dashboard-page" style={{ margin: '0 auto', width: '100%' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
          {/* #976: `minWidth: 200` was an UNCONDITIONAL floor, so in a panel narrower
              than 200px + the page's 44px padding this block pushed the dashboard
              wider than the panel it sits in — the same "not width fitted" the owner
              reported, arriving from the greeting row instead of the `.dash` grid.
              `min(200px, 100%)` keeps the 200px floor wherever the panel can afford
              it and collapses to the panel width where it cannot. Measured at a
              560px tree on a 1280px viewport: this block's right edge was 222px in a
              220px scroller. */}
          <div style={{ flex: 1, minWidth: 'min(200px, 100%)' }}>
            <span className="t-page">{greeting()}, {name}</span>
            <span className="t-body muted" style={{ marginLeft: 8 }}>
              {m
                ? (m.unverified.total === 0
                  ? 'Everything is verified — nice work.'
                  : `${m.unverified.total} requirement${m.unverified.total === 1 ? '' : 's'} need${m.unverified.total === 1 ? 's' : ''} attention.`)
                : 'Loading your workspace…'}
            </span>
          </div>
          {projects.length > 0 && (
            <select
              data-testid="dashboard-project-selector"
              value={selectedProject || ''}
              onChange={handleProjectChange}
              className="input"
              /* #976: `minWidth: 260` overflowed a narrow panel by up to 106px —
                 measured 282px of content in a 220px scroller with the tree dragged
                 to 560px. The selector is the widest single element on the page, and
                 nothing above it can shrink a hard px floor. `min(260px, 100%)` keeps
                 the comfortable 260px on a normal panel; `maxWidth: '100%'` stops the
                 400px cap overhanging for the same reason. */
              // #1034: this block's OWN `padding: '7px 12px'` was the proximate cause of
              // the reported clipping. `.input`'s box is a fixed 28px with NO padding of
              // its own; a `<select>`'s internal text always renders at the browser's
              // native `line-height:normal` (author CSS cannot override it — see ds.css
              // #1034), so adding 7px top+bottom here left too little content box for
              // that native line box, and the tops of glyphs were sliced off ("E2E
              // Default" rendered as "F2F Default"). Horizontal-only padding matches
              // every other `.input` control in the app, and ds.css's `.input` is now
              // `min-height` (not `height`) as a second, independent layer of defence —
              // even a call site that DOES need vertical padding now grows instead of
              // clipping.
              style={{ minWidth: 'min(260px, 100%)', maxWidth: 'min(400px, 100%)', padding: '0 12px', fontSize: 13, borderRadius: 6, border: '1px solid var(--border)', background: 'var(--base)', color: 'var(--text)' }}
            >
              {/* #439: "Whole organisation" is always the first item, but never
                  the default selection (see the scope-resolution effect). */}
              <option value="">Whole organisation — all projects</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          )}
          {/* #381: project actions — mirror the tree's project right-click menu.
              Only shown when a SPECIFIC project is selected (not "All projects"). */}
          {selectedProject && (
            <ProjectActionBar
              projectId={selectedProject}
              projectName={selectedProjectName}
              onError={(msg) => setError(msg)}
              onChanged={onProjectChanged}
            />
          )}
        </div>

        {error && <div className="cardp" style={{ color: 'var(--error)' }}>{error}</div>}

        {/* KPI strip */}
        <div className="stats">
          <div className="stat">
            <div className="lab"><Ic name="tree" sm />Requirements</div>
            <div className="num">{m ? m.total : '—'}</div>
            <div className="delta muted3">{m ? `${m.containers} container${m.containers === 1 ? '' : 's'}` : ''}</div>
          </div>
          <div className="stat">
            <div className="lab"><Ic name="check" sm />Verification</div>
            <div className="num" style={{ color: 'var(--success)' }}>{m ? `${m.pct}%` : '—'}</div>
            <div className="delta" style={{ color: 'var(--success)' }}>{m ? `${m.verified} verified` : ''}</div>
          </div>
          <div className="stat">
            <div className="lab"><Ic name="shield" sm />Coverage gaps</div>
            <div className="num" style={{ color: 'var(--warning)' }}>{m ? m.unverified.total : '—'}</div>
            <div className="delta" style={{ color: 'var(--warning)' }}>needs verification</div>
          </div>
          <div className="stat">
            <div className="lab"><Ic name="folder" sm />Containers</div>
            <div className="num">{m ? m.containers : '—'}</div>
            <div className="delta muted3">groups</div>
          </div>
        </div>

        <div className="dash">
          {/* Needs your attention */}
          {/* F-21 / REQ-6107 (#581, D-01): the "Verify all (N)" bulk button and the
              per-row "Verify" action are DELETED, not renamed to "Approve all" —
              owner-decided: one-click mass sign-off doesn't fit the sign-off
              semantics #590/#591 are strengthening. Replaced (eventually) by a
              filtered review queue, #146 — not by this card. The list/count below
              reads `m.unverified`, which as of #581 B1 is server-computed from the
              `item_approvals` rollup (`project_summary.py` / `feedback.kpi_verified`),
              not the retired status attribute. */}
          <div className="cardp">
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
              <span className="t-sec" style={{ flex: 1 }}>Needs your attention</span>
              {m && m.unverified.total > 0 && <span className="badge accent">{m.unverified.total}</span>}
            </div>
            {!m && <div className="t-body muted3" style={{ padding: '8px 0' }}>Loading…</div>}
            {m && m.unverified.total === 0 && (
              <div className="t-body muted3" style={{ padding: '8px 0' }}>Everything is verified — nothing needs attention. 🎉</div>
            )}
            {m && m.unverified.rows.map((r) => (
              <div className="qrow" key={r.id}>
                <span className="qic" style={{ background: 'var(--warning-bg)', color: 'var(--warning)' }}><Ic name="shield" sm /></span>
                <a
                  className="qt"
                  href={itemHref(r.id)}
                  onClick={navClickHandler(() => goItem(r.id))}
                >
                  <div className="tt">{r.title || 'Untitled'}</div>
                  <div className="mt" title={resolveIdDisplay(r, showHumanIds).secondary || ''}>{resolveIdDisplay(r, showHumanIds).primary}{r.area ? ` · ${r.area}` : ''}{r.status ? ` · ${r.status}` : ' · no status'}</div>
                </a>
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  <a className="btn sm" href={itemHref(r.id)} onClick={navClickHandler(() => goItem(r.id))}>Open</a>
                </div>
              </div>
            ))}
          </div>

          {/* Pending archive approvals */}
          {m && m.pendingArchive.total > 0 && (
            <div className="cardp" style={{ gridColumn: '1 / -1' }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
                <span className="t-sec" style={{ flex: 1 }}>Pending archive approval</span>
                <button type="button"
                  className="btn sm"
                  data-testid="archive-all"
                  style={{ color: 'var(--error)', marginRight: 8 }}
                  onClick={handleArchiveAll}
                ><Ic name="archive" sm /> Archive all ({m.pendingArchive.total})</button>
                <span className="badge" style={{ background: 'var(--error-bg)', color: 'var(--error)' }}>{m.pendingArchive.total}</span>
              </div>
              {m.pendingArchive.rows.slice(0, 6).map((r) => (
                <div className="qrow" key={r.id}>
                  <span className="qic" style={{ background: 'var(--error-bg)', color: 'var(--error)' }}><Ic name="archive" sm /></span>
                  <a
                    className="qt"
                    href={itemHref(r.id)}
                    onClick={navClickHandler(() => goItem(r.id))}
                  >
                    <div className="tt">{r.title || 'Untitled'}</div>
                    <div className="mt" title={resolveIdDisplay(r, showHumanIds).secondary || ''}>
                      {resolveIdDisplay(r, showHumanIds).primary}{r.area ? ` · ${r.area}` : ''}
                      {r.archive_reason ? ` · ${r.archive_reason}` : ''}
                    </div>
                  </a>
                  <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                    <button type="button" className="btn sm" style={{ color: 'var(--error)' }} onClick={() => handleConfirmArchive(r.id)}>Archive</button>
                    <button type="button" className="btn sm" onClick={() => handleCancelArchive(r.id)}>Cancel</button>
                  </div>
                </div>
              ))}
              {m.pendingArchive.total > 6 && (
                <div className="t-body muted3" style={{ padding: '4px 0 0', fontSize: 12 }}>
                  +{m.pendingArchive.total - 6} more pending
                </div>
              )}
            </div>
          )}

          {/* Right column */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div className="cardp">
              <div className="t-sec" style={{ marginBottom: 8 }}>Coverage by level</div>
              {!m && <div className="t-body muted3">Loading…</div>}
              {m && m.areas.length === 0 && <div className="t-body muted3">No requirements yet.</div>}
              {m && m.areas.map((a) => (
                <div className="lvl" key={a.label}>
                  <span className="muted" title={a.label} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.label}</span>
                  <div className="bar"><i style={{ width: `${a.pct}%`, background: barColor(a.pct) }} /></div>
                  <span className="muted3" style={{ textAlign: 'right' }}>{a.pct}%</span>
                </div>
              ))}
            </div>

            {/* Review feedback — open review comments in the selected project (#342). */}
            <div className="cardp" data-testid="dashboard-feedback-card">
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 6 }}>
                <span className="t-sec" style={{ flex: 1 }}>Review feedback</span>
                {feedback && feedback.comments.length > 0 && (
                  <span className="badge accent" data-testid="dashboard-feedback-count">{feedback.comments.length}</span>
                )}
              </div>
              {!selectedProject && (
                <div className="t-body muted3" style={{ padding: '4px 0' }}>
                  Select a project to see its open review comments.
                </div>
              )}
              {/* The failure state has to come FIRST and suppress the others: while it
                  is showing, `feedback` is null, and the "Loading…" below would otherwise
                  claim the fetch is still running forever. */}
              {selectedProject && feedbackError && (
                <div className="t-body" style={{ padding: '4px 0', color: 'var(--error)' }}
                     data-testid="dashboard-feedback-error"
                >
                  Couldn’t load review feedback.
                  {' '}
                  <button
                    type="button"
                    className="linkbtn"
                    data-testid="dashboard-feedback-retry"
                    onClick={() => setFeedbackReload((n) => n + 1)}
                  >
                    Retry
                  </button>
                </div>
              )}
              {selectedProject && !feedback && !feedbackError && <div className="t-body muted3">Loading…</div>}
              {selectedProject && feedback && feedback.comments.length === 0 && (
                <div className="t-body muted3" style={{ padding: '4px 0' }} data-testid="dashboard-feedback-empty">
                  No open review feedback. 🎉
                </div>
              )}
              {selectedProject && feedback && feedback.comments.slice(0, 5).map((c) => (
                <div className="qrow" key={c.comment_id}>
                  <span className="qic" style={{ background: 'var(--warning-bg)', color: 'var(--warning)' }}><Ic name="bell" sm /></span>
                  <a
                    className="qt"
                    href={itemHref(c.item_uuid)}
                    onClick={navClickHandler(() => goItem(c.item_uuid))}
                  >
                    <div className="tt" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.body}</div>
                    <div className="mt">{c.raised_by}{c.kind === 'overall' ? ' · review summary' : ''}</div>
                  </a>
                  <a className="btn sm" href={itemHref(c.item_uuid)} onClick={navClickHandler(() => goItem(c.item_uuid))}>Open</a>
                </div>
              ))}
              {selectedProject && feedback && feedback.truncated && (
                <div className="t-body muted3" style={{ padding: '4px 0 0', fontSize: 12 }}>
                  Showing the first {feedback.comments.length} — more open comments exist.
                </div>
              )}
            </div>

            <div className="cardp">
              <div className="t-sec" style={{ marginBottom: 6 }}>Recent activity</div>
              {!m && <div className="t-body muted3">Loading…</div>}
              {m && m.recent.length === 0 && <div className="t-body muted3">No activity yet.</div>}
              {m && m.recent.length > 0 && (
                <div className="tl">
                  {m.recent.map((r) => {
                    const ok = !!r.verified;
                    return (
                      <div className="tlitem" key={r.id}>
                        <span className={`tldot${ok ? '' : ' muted'}`} />
                        <a
                          className="t-strong"
                          href={itemHref(r.id)}
                          style={{ fontSize: 12, cursor: 'pointer', display: 'block', textDecoration: 'none', color: 'inherit' }}
                          onClick={navClickHandler(() => goItem(r.id))}
                        >
                          {r.title || 'Untitled'}
                          {ok && <span className="badge success" style={{ marginLeft: 4 }}>Verified</span>}
                        </a>
                        <div className="t-body muted3" style={{ fontSize: 11 }} title={r.updated_at || resolveIdDisplay(r, showHumanIds).secondary || ''}>
                          {resolveIdDisplay(r, showHumanIds).primary}{r.is_container ? ' · container' : ''}
                          {r.updated_at ? ` · ${formatRelativeTime(r.updated_at)}` : ''}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Coverage/quality/breakdown are SINGLE-PROJECT analyses — for the org-wide
            "Whole organisation" scope the backend omits `metrics`, so instead of
            showing meaningless numbers we prompt the user to scope to a project. The
            KPI strip, needs-attention, coverage-by-level and recent activity above DO
            aggregate org-wide, so they stay. */}
        {m && !m.metrics && (
          <div className="metrics" data-testid="dashboard-metrics-orgwide-hint">
            <div className="cardp t-body muted3" style={{ padding: '14px 16px' }}>
              Select a specific project (top of the selector) to see coverage, review
              and breakdown metrics — these are computed per project, not across the
              whole organisation.
            </div>
          </div>
        )}

        {/* #441: holistic metric cards. Counts render from the cheap summary payload;
            each card's detail lazy-loads on first expand (Isolated / Leaf-gap /
            Links-by-type share one /coverage fetch; Review-staleness reads /quality). */}
        {m && m.metrics && (
          <div className="metrics" data-testid="dashboard-metrics">
            <MetricCard
              testId="metric-isolated"
              icon="◌"
              title="Isolated requirements"
              badge={m.metrics.isolated}
              badgeTone={m.metrics.isolated > 0 ? 'warning' : 'neutral'}
              open={openCards.has('isolated')}
              onOpenChange={(o) => setCardOpen('isolated', o)}
              loading={coverage.state === 'loading'}
              error={coverage.state === 'error' ? coverage.error : null}
              ready={coverage.state === 'ready'}
            >
              <ItemList
                items={coverage.data?.isolated_requirements}
                total={m.metrics.isolated}
                showHumanIds={showHumanIds}
                itemHref={itemHref}
                onNavigate={goItem}
              />
            </MetricCard>

            <MetricCard
              testId="metric-leaf-gap"
              icon="⊘"
              title="Leaf-coverage gap"
              badge={m.metrics.leaf_gap}
              badgeTone={m.metrics.leaf_gap > 0 ? 'error' : 'neutral'}
              open={openCards.has('leaf-gap')}
              onOpenChange={(o) => setCardOpen('leaf-gap', o)}
              loading={coverage.state === 'loading'}
              error={coverage.state === 'error' ? coverage.error : null}
              ready={coverage.state === 'ready'}
            >
              <ItemList
                items={coverage.data?.leaf_gap_requirements}
                total={m.metrics.leaf_gap}
                showHumanIds={showHumanIds}
                itemHref={itemHref}
                onNavigate={goItem}
              />
            </MetricCard>

            <MetricCard
              testId="metric-links"
              icon="⇄"
              title="Links by type"
              badge={`${m.metrics.link_types} type${m.metrics.link_types === 1 ? '' : 's'}`}
              badgeTone="neutral"
              open={openCards.has('links')}
              onOpenChange={(o) => setCardOpen('links', o)}
              loading={coverage.state === 'loading'}
              error={coverage.state === 'error' ? coverage.error : null}
              ready={coverage.state === 'ready'}
            >
              {(() => {
                const dist = coverage.data?.link_type_distribution || {};
                const entries = Object.entries(dist).sort((a, b) => b[1] - a[1]);
                if (entries.length === 0) {
                  return <div className="t-body muted3" style={{ padding: '10px 0' }}>No links yet.</div>;
                }
                return (
                  <>
                    <div className="hscroll" data-testid="metric-links-scroll">
                      {entries.map(([type, n]) => (
                        <button
                          type="button"
                          className="chip clickable"
                          key={type}
                          data-testid={`link-type-chip-${type}`}
                          title={`${n} "${type}" link${n === 1 ? '' : 's'} — show the requirements involved`}
                          onClick={() => setLinkTypeDrawer({ type, links: n })}
                        >
                          {type} · {n}
                        </button>
                      ))}
                    </div>
                    <div className="scrollnote">↔ scrolls sideways · click a count to see the matching requirements →</div>
                  </>
                );
              })()}
            </MetricCard>

            <MetricCard
              testId="metric-review-stale"
              icon="◷"
              title="Review staleness"
              badge={`${m.metrics.review_stale} stale`}
              badgeTone={m.metrics.review_stale > 0 ? 'warning' : 'neutral'}
              open={openCards.has('review-stale')}
              onOpenChange={(o) => setCardOpen('review-stale', o)}
              loading={quality.state === 'loading'}
              error={quality.state === 'error' ? quality.error : null}
              ready={quality.state === 'ready'}
            >
              <ItemList
                items={quality.data?.stale_requirements}
                total={m.metrics.review_stale}
                noteOf={(it) => (it.age_days != null ? `${it.age_days}d` : null)}
                showHumanIds={showHumanIds}
                itemHref={itemHref}
                onNavigate={goItem}
              />
            </MetricCard>

            {/* #442: Breakdown & review — at the BOTTOM because it opens BIG. The full
                PR-style review (indented sub-requirements, approve pills, comment
                threads, review filter — the review.html layout) mounts one BreakdownTab
                per top-level item, LAZY: the /breakdown subtree is the app's most
                expensive call, so it never runs until the user expands this card. */}
            {m.roots.length > 0 && (
              <details
                className="metric"
                data-testid="metric-breakdown"
                open={openCards.has('breakdown')}
                onToggle={(e) => setCardOpen('breakdown', e.currentTarget.open)}
              >
                <summary className="mhead">
                  <span className="mi" aria-hidden="true">☰</span>
                  <span className="mt">Breakdown &amp; review</span>
                  <span className="badge neutral" data-testid="metric-breakdown-count">
                    {m.total} req{m.total === 1 ? '' : 's'}
                  </span>
                  {feedback && feedback.comments.length > 0 && (
                    <span className="badge warning">{feedback.comments.length} open</span>
                  )}
                  <svg className="chev2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <path d="M9 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </summary>
                <div className="mbody" data-detail>
                  {/* Mount (and therefore fetch) the heavy review subtree only once
                      the card is actually open — one BreakdownTab per root, exactly
                      like ProjectDetails' "Full breakdown" (#410). */}
                  {openCards.has('breakdown') && m.roots.map((root) => (
                    <div key={root.id} data-testid={`breakdown-${root.id}`} style={{ marginBottom: 18 }}>
                      {/* #442 fix: label each root section so it's clear which
                          container/tree the breakdown below belongs to. */}
                      <a
                        className="t-sec"
                        href={itemHref(root.id)}
                        onClick={navClickHandler(() => goItem(root.id))}
                        style={{ display: 'flex', alignItems: 'baseline', gap: 8, margin: '4px 0 6px', textDecoration: 'none', color: 'inherit' }}
                        title={resolveIdDisplay(root, showHumanIds).secondary || ''}
                      >
                        <span>{root.title || 'Untitled'}</span>
                        <span className="idchip">{resolveIdDisplay(root, showHumanIds).primary}</span>
                      </a>
                      <Box sx={{ height: 560, border: 1, borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
                        <BreakdownTab
                          itemId={root.id}
                          onNavigateToItem={goItem}
                          reviewChangeCount={reviewChangeCount}
                        />
                      </Box>
                    </div>
                  ))}
                </div>
              </details>
            )}
          </div>
        )}
      </div>

      {/* In-app confirmation modal for dashboard actions (replaces native alerts):
          cascade archive (#347), Archive all / Verify all (#348). */}
      <ConfirmDialog
        open={!!confirm}
        onClose={() => setConfirm(null)}
        onConfirm={() => { const c = confirm; setConfirm(null); if (c) c.onConfirm(); }}
        title={confirm?.title || ''}
        message={confirm?.message || ''}
        severity={confirm?.severity || null}
        confirmColor={confirm?.confirmColor || 'primary'}
        confirmLabel={confirm?.confirmLabel || 'Confirm'}
        testId={confirm?.testId || 'dashboard-confirm-dialog'}
      />

      {/* #449: "Links by type" chip -> docked drawer of matching requirements.
          Mounted once, driven by linkTypeDrawer ({ type, links } | null); stays
          in the DOM across open/close so its slide transition can play.
          #558: `links` is the chip's LINK-ROW count — a different axis from the
          drawer's distinct-item total, so it is passed as linkCount (labelled),
          never seeded into the item total. */}
      <LinkTypeDrawer
        open={!!linkTypeDrawer}
        linkType={linkTypeDrawer?.type}
        linkCount={linkTypeDrawer?.links}
        projectId={selectedProject || null}
        onClose={() => setLinkTypeDrawer(null)}
        onNavigate={goItem}
      />
    </div>
  );
}
